import { message, notification } from 'antd';
import { showErrorModal, errorData, errorList } from 'apollo/cache';

// Tipos mejorados para errores
export interface CodeMessage {
  code: string;
  message?: string;
}

export interface ExtendedErrorContext {
  originalMessage?: string;
  service?: string | null;
  provider?: string | null;
  path?: string | null;
  field?: string | null;
  timestamp?: string;
}

export interface CustomError {
  type: string;
  name: string;
  message?: string;
  codeMessage?: CodeMessage;
  status?: number;
  context?: ExtendedErrorContext;
  fieldErrors?: Record<string, string[]>;
  requestId?: string;
}

export interface ErrorQuery {
  errorData: CustomError;
  showErrorModal: boolean;
}

const messageDuration = 6; // Aumentado a 6 segundos para dar más tiempo de lectura

/**
 * Manejador de errores mejorado para Apollo GraphQL
 */
const ErrorHandler = (error: CustomError): void => {
  // Guardar el error en la lista histórica
  errorList([...errorList(), error]);

  const errorTitles: Record<string, string> = {
    FATAL: 'Error Fatal',
    VALIDATION: 'Error de Validación',
    NOT_FOUND: 'Recurso No Encontrado',
    UNAUTHORIZED: 'No Autorizado',
    FORBIDDEN: 'Acceso Prohibido',
    SERVICE: 'Error del Servicio',
    EXTERNAL_PROVIDER: error.context?.provider 
      ? `Error del Proveedor Externo: ${error.context.provider}` 
      : 'Error del Proveedor Externo',
    BANNER: 'Error de Banner',
  };

  const errorTitle = errorTitles[error.type] || `Error ${error.name}`;

  const errorMsg = (error.codeMessage && error.codeMessage.message)
    ? error.codeMessage.message
    : error.message || 'Se produjo un error desconocido';

  const errorMsgWithProvider = error.context?.provider
    ? `${error.context.provider} - ${errorMsg}` 
    : errorMsg;
  
  const devDetails = {
    timestamp: error.context?.timestamp || new Date().toISOString(),
    requestId: error.requestId || 'N/A',
    path: error.context?.path || 'N/A',
    service: error.context?.service || 'N/A',
    provider: error.context?.provider || 'N/A',
  };

  // Determinar cómo mostrar el error según su tipo
  if (error.type === 'FATAL') {
    // Errores fatales van al modal
    const parsedError = {
      ...error,
      name: error.codeMessage ? error.codeMessage.code : error.name,
      message: error.codeMessage?.message || error.message || 'Error del servidor',
    };

    showErrorModal(true);
    errorData(parsedError);

    // También mostrar una notificación persistente
    notification.error({
      message: errorTitle,
      description: errorMsg,
      duration: 0, // Persistente
      onClick: () => notification.destroy(),
    });
  }
  else if (error.type === 'VALIDATION') {
    // Errores de validación mostrar con los campos específicos
    if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
      // Si hay errores por campo, mostrarlos individualmente
      Object.entries(error.fieldErrors).forEach(([field, errors]) => {
        message.error(`${field}: ${errors.join(', ')}`, messageDuration);
      });
    } else {
      // Sino, mostrar el mensaje general
      message.error(errorMsg, messageDuration);
    }

    errorData(error);
  }
  else if (error.type === 'NOT_FOUND') {
    // Errores not found
    message.warning(errorMsg, messageDuration);
    errorData(error);
  }
  else if (error.type === 'EXTERNAL_PROVIDER') {
    // Errores not found
    // message.warning(errorMsgWithProvider, messageDuration);
    errorData(error);
    notification.error({
      message: errorTitle,
      description: errorMsg,
      duration: 0, // Persistente
      onClick: () => notification.destroy(),
    });
  }
  else if (error.type === 'UNAUTHORIZED' || error.type === 'FORBIDDEN') {
    // Errores de autenticación/autorización
    notification.warning({
      message: errorTitle,
      description: errorMsg,
      duration: messageDuration
    });

    errorData(error);

    // Opcional: Redirigir al login si es un error de autenticación
    // if (error.type === 'UNAUTHORIZED') {
    //   setTimeout(() => {
    //     window.location.href = '/login';
    //   }, 2000);
    // }
  }
  else {
    // Otros tipos de error (BANNER, SERVICE, etc.)
    errorData(error);

    const bannerMessage = error.codeMessage
      ? `${errorTitle}: ${errorMsg}`
      : errorMsg;

    message.warning(bannerMessage, messageDuration, () => errorData(null));

    // En ambiente de desarrollo, mostrar más detalles
    if (process.env.NODE_ENV === 'development') {
      console.table(devDetails);
    }
  }
};

// Función auxiliar para formatear errores de campos específicos
export const formatFieldErrors = (fieldErrors?: Record<string, string[]>): string => {
  if (!fieldErrors) return '';

  return Object.entries(fieldErrors)
    .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
    .join('\n');
};

export default ErrorHandler;